import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Button from '../../components/button/Button';
import { cardsBrand } from '../../components/card/constants';
import Icon from '../../components/icon/Icon';
import List from '../../components/list/List';
import Loading from '../../components/loading/Loading';
import {
  getJobReceipt,
  getMembershipReceipt,
  getUserProfile,
  showModal,
} from '../../redux/actions';

import T from 'prop-types';
import { useSearchParam } from 'react-use';
import { PROJECT_TRIAL_MEMBERS_MODAL } from '../../components/modal/ProjectTrialMembers/ProjectTrialMembers';
import TextDivider from '../../components/text/TextDivider';
import useCurrentProject from '../../hooks/useCurrentProject';
import routes from '../../routes/constants';
import text, { formatCurrency } from '../../text';
import { formatDate } from '../../utilities/date';
import { isEmpty } from 'lodash';
import useUrlParam from '@/mp/hooks/useUrlParam';
import useQueryParam from '@/mp/hooks/useQueryParam';

export const ReceiptItem = ({ receipt, description }) => {
  return (
    <>
      <tbody className='ReceiptItem'>
        <List.Item
          description={<h3>{description}</h3>}
          value={
            <h3>
              {formatCurrency(
                receipt.value?.amount,
                receipt.value?.currency.name,
                receipt.value?.currency.scale_factor,
                {},
                true
              )}
            </h3>
          }
        />
      </tbody>
      <div className='d-flex flex-row flex-gap-12'>
        <Link
          to={{ pathname: receipt.value?.receipt_url }}
          className='mb-3'
          target='_blank'
          rel='noopener noreferrer'
        >
          {text('viewReceipt', {
            receiptNumber: receipt.value?.receipt_number,
          })}
        </Link>
        {receipt.value?.invoice_url && (
          <>
            <span>|</span>
            <Link
              to={{ pathname: receipt.value?.invoice_url }}
              className='mb-3'
              target='_blank'
              rel='noopener noreferrer'
            >
              View invoice
            </Link>
          </>
        )}
      </div>
    </>
  );
};

ReceiptItem.propTypes = {
  receipt: T.object,
  description: T.object,
};

const PaymentSuccess = () => {
  const isTrial = useSearchParam('isTrial');
  const jobId = useUrlParam('jobId', { int: true });
  const orderPaymentIntentId = useQueryParam('orderPaymentIntentId');
  const membershipPaymentIntentId = useQueryParam('membershipPaymentIntentId');

  const dispatch = useDispatch();
  useCurrentProject({ enableJob: true });
  const { currentProject, orderReceipt, membershipReceipt } = useSelector(
    (state) => ({
      orderReceipt: state.jobsReducer.receipt,
      membershipReceipt: state.profileReducer.membershipReceipt,
      currentProject: state.project.currentProject,
    })
  );

  useEffect(() => {
    if (jobId && orderPaymentIntentId) {
      dispatch(getJobReceipt(jobId, orderPaymentIntentId));
    }
  }, [dispatch, jobId, orderPaymentIntentId]);

  useEffect(() => {
    dispatch(getUserProfile());
  }, [dispatch]);

  useEffect(() => {
    if (membershipPaymentIntentId) {
      dispatch(getMembershipReceipt(membershipPaymentIntentId));
    }
  }, [dispatch, membershipPaymentIntentId]);

  return (
    <div className='PaymentSuccess'>
      {!orderReceipt.isLoading || !membershipReceipt.isLoading ? (
        <>
          <Icon icon='circled-tick_filled' />
          <div className='w-100'>
            <h3 className='text-center'>{text('thanksForPayment')}</h3>
            <TextDivider isComplete isSecondary />
          </div>
          <List>
            {orderReceipt ? (
              <ReceiptItem
                receipt={orderReceipt}
                description={text('survey')}
              />
            ) : null}
            {!isEmpty(membershipReceipt?.value) ? (
              <ReceiptItem
                receipt={membershipReceipt}
                description={text('membership')}
              />
            ) : null}
          </List>
          <div>{text('receiptEmailSent')}</div>
          <TextDivider isComplete isSecondary />
          <div className='full-width'>
            <List isPaddingless>
              <List.Item
                description={text('paymentDate')}
                value={
                  orderReceipt.value
                    ? formatDate(orderReceipt.value?.payment_date)
                    : null
                }
              />
              <List.Item
                description={text('paymentMethod')}
                value={cardsBrand[orderReceipt.value?.card.brand]}
              />
            </List>
          </div>
          <TextDivider isComplete isSecondary />
          <p>{text('sharePlatformWithTeam')}</p>
          <Button
            autoFocus
            className='full-width'
            onClick={() =>
              dispatch(
                showModal(PROJECT_TRIAL_MEMBERS_MODAL, {
                  projectId: currentProject.id,
                })
              )
            }
          >
            {text('proceedToShare')}
          </Button>
          <a className='mt-3' name='cancel' href={routes.dashboard}>
            {text('goToDashboard')}
          </a>
        </>
      ) : (
        <Loading />
      )}
    </div>
  );
};

export default PaymentSuccess;
